import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  XMarkIcon,
  ExclamationTriangleIcon,
  SparklesIcon,
  CheckCircleIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import Button from '../ui/Button';

/**
 * TrialExpirationModal - Shows warning when trial is expiring
 *
 * Displays at specific intervals:
 * - 7 days before expiration
 * - 3 days before expiration
 * - 1 day before expiration
 * - On expiration day
 *
 * Uses localStorage to track when warnings were shown to avoid spam
 */
const TrialExpirationModal = ({ subscriptionDetails, onClose }) => {
  const navigate = useNavigate();
  const [daysRemaining, setDaysRemaining] = useState(0);
  const [shouldShow, setShouldShow] = useState(false);
  const [warningLevel, setWarningLevel] = useState('info'); // 'info', 'warning', 'critical', 'expired'

  useEffect(() => {
    if (!subscriptionDetails || subscriptionDetails.status !== 'trial') {
      setShouldShow(false);
      return;
    }

    const now = new Date();
    const endDate = new Date(subscriptionDetails.trialEndDate);
    const diffMs = endDate - now;
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    setDaysRemaining(days);

    // Determine warning level based on days remaining
    let level = 'info';
    let shouldShowModal = false;

    if (days <= 0) {
      level = 'expired';
      shouldShowModal = true;
    } else if (days === 1) {
      level = 'critical';
      shouldShowModal = checkAndSetLastShown('1day');
    } else if (days === 3) {
      level = 'warning';
      shouldShowModal = checkAndSetLastShown('3day');
    } else if (days === 7) {
      level = 'info';
      shouldShowModal = checkAndSetLastShown('7day');
    }

    setWarningLevel(level);
    setShouldShow(shouldShowModal);
  }, [subscriptionDetails]);

  // Check if warning was already shown for this interval
  const checkAndSetLastShown = (interval) => {
    const storageKey = `trial_warning_${interval}_${subscriptionDetails?.trialEndDate}`;
    const lastShown = localStorage.getItem(storageKey);
    const today = new Date().toDateString();

    // If not shown today for this interval, mark it and show
    if (lastShown !== today) {
      localStorage.setItem(storageKey, today);
      return true;
    }

    return false;
  };

  const handleUpgradeClick = () => {
    navigate('/billing');
    if (onClose) onClose();
  };

  const handleDismiss = () => {
    if (onClose) onClose();
  };

  const handleRemindLater = () => {
    // Set a flag to remind again in 4 hours
    const storageKey = `trial_remind_later_${subscriptionDetails?.trialEndDate}`;
    const remindTime = new Date().getTime() + (4 * 60 * 60 * 1000); // 4 hours from now
    localStorage.setItem(storageKey, remindTime.toString());
    if (onClose) onClose();
  };

  if (!shouldShow) {
    return null;
  }

  // Get plan display name
  const getPlanDisplayName = (planId) => {
    const planNames = {
      'email-hosting': 'Basic Email Hosting',
      'iris-pro': 'Iris Pro',
      'iris-autonomous': 'Iris Autonomous'
    };
    return planNames[planId] || planId;
  };

  const planName = getPlanDisplayName(subscriptionDetails.plan);
  const endDate = new Date(subscriptionDetails.trialEndDate);

  // Configure modal appearance based on warning level
  const config = {
    expired: {
      icon: ExclamationTriangleIcon,
      iconColor: 'bg-red-500',
      borderColor: 'border-red-500',
      title: 'Your Trial Has Expired',
      message: 'Your trial period has ended. Please upgrade to a paid plan to continue using all features.',
      buttonText: 'Upgrade Now',
      buttonVariant: 'destructive'
    },
    critical: {
      icon: ExclamationTriangleIcon,
      iconColor: 'bg-orange-500',
      borderColor: 'border-orange-500',
      title: 'Trial Expiring in 1 Day',
      message: `Your ${planName} trial will expire tomorrow. Upgrade now to avoid any service interruption.`,
      buttonText: 'Upgrade to Continue',
      buttonVariant: 'primary'
    },
    warning: {
      icon: ClockIcon,
      iconColor: 'bg-yellow-500',
      borderColor: 'border-yellow-500',
      title: 'Trial Expiring in 3 Days',
      message: `Your ${planName} trial expires in 3 days. Consider upgrading to maintain uninterrupted access.`,
      buttonText: 'View Plans',
      buttonVariant: 'primary'
    },
    info: {
      icon: SparklesIcon,
      iconColor: 'bg-blue-500',
      borderColor: 'border-blue-500',
      title: 'Trial Status Update',
      message: `You have ${daysRemaining} days remaining in your ${planName} trial. Upgrade anytime to continue after your trial ends.`,
      buttonText: 'Explore Plans',
      buttonVariant: 'primary'
    }
  };

  const currentConfig = config[warningLevel];
  const IconComponent = currentConfig.icon;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 transition-opacity"
        onClick={handleDismiss}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className={`bg-white dark:bg-[#1a1a1a] rounded-xl shadow-2xl border-2 ${currentConfig.borderColor} max-w-md w-full transform transition-all`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header with Icon */}
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-4">
                <div className={`${currentConfig.iconColor} p-3 rounded-lg`}>
                  <IconComponent className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                    {currentConfig.title}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {currentConfig.message}
                  </p>
                </div>
              </div>
              <button
                onClick={handleDismiss}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-4">
            {/* Trial Info */}
            <div className="bg-gray-50 dark:bg-[#222222] rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Current Plan
                </span>
                <span className="text-sm font-semibold text-gray-900 dark:text-white">
                  {planName}
                </span>
              </div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Status
                </span>
                <span className={`text-sm font-semibold ${
                  warningLevel === 'expired'
                    ? 'text-red-600 dark:text-red-400'
                    : 'text-blue-600 dark:text-blue-400'
                }`}>
                  {warningLevel === 'expired' ? 'Expired' : 'Trial'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {warningLevel === 'expired' ? 'Expired On' : 'Expires On'}
                </span>
                <span className="text-sm font-semibold text-gray-900 dark:text-white">
                  {endDate.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
              </div>
            </div>

            {/* Benefits of upgrading */}
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                Why Upgrade?
              </h4>
              <ul className="space-y-2">
                {[
                  'Uninterrupted access to all features',
                  'Priority customer support',
                  'Advanced AI capabilities',
                  'Team collaboration tools'
                ].map((benefit, index) => (
                  <li key={index} className="flex items-start space-x-2">
                    <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {benefit}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="p-6 border-t border-gray-200 dark:border-gray-700 space-y-3">
            <Button
              onClick={handleUpgradeClick}
              variant={currentConfig.buttonVariant}
              className="w-full font-medium"
            >
              <SparklesIcon className="w-5 h-5 mr-2" />
              {currentConfig.buttonText}
            </Button>

            {warningLevel !== 'expired' && (
              <div className="flex space-x-3">
                <Button
                  onClick={handleRemindLater}
                  variant="secondary"
                  className="flex-1"
                >
                  Remind Me Later
                </Button>
                <Button
                  onClick={handleDismiss}
                  variant="ghost"
                  className="flex-1"
                >
                  Dismiss
                </Button>
              </div>
            )}

            {warningLevel === 'expired' && (
              <Button
                onClick={handleDismiss}
                variant="ghost"
                className="w-full"
              >
                Close
              </Button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default TrialExpirationModal;
